import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  type?: string;
  name?: string;
  image?: string;
  path?: string;
  keywords?: string[];
  canonicalUrl?: string;
  noindex?: boolean;
  publishDate?: string;
  lastModified?: string;
  location?: {
    city?: string;
    state?: string;
    zip?: string;
  };
}

export function SEO({
  title,
  description,
  type = 'website',
  name = 'XXL IT Services',
  image = 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80&w=1200&h=630',
  path = '',
  keywords = [],
  canonicalUrl: customCanonicalUrl,
  noindex = false,
  publishDate,
  lastModified,
  location = {
    city: 'Meridian',
    state: 'ID',
    zip: '83646'
  }
}: SEOProps) {
  const routerLocation = useLocation();
  const domain = 'xxlservices.com';
  const siteUrl = `https://${domain}`;

  // Use actual pathname from React Router, strip trailing slash
  const pathname = routerLocation.pathname.replace(/\/$/, '') || '/';

  // Generate canonical URL: always without trailing slash (except root)
  const canonicalUrl = customCanonicalUrl || (pathname === "/"
    ? siteUrl
    : `${siteUrl}${pathname}`);
  
  // Default keywords for all pages
  const defaultKeywords = [
    'Commercial Low Voltage Contractor',
    'Structured Cabling',
    'Fiber Optic Installation',
    'UniFi Protect Security Cameras',
    'UniFi Access Control',
    'Business Wi-Fi',
    'Network Installation',
    'Managed IT Services',
    location.city,
    'Idaho',
    'Treasure Valley'
  ];

  // Combine default and page-specific keywords
  const allKeywords = [...new Set([...defaultKeywords, ...keywords])].join(', ');
  

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{`${title} | ${name}`}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      
      {/* Search engine verification - set actual codes in environment or Search Console */}
      
      {/* Publishing dates for articles */}
      {publishDate && <meta name="article:published_time" content={publishDate} />}
      {lastModified && <meta name="article:modified_time" content={lastModified} />}
      {publishDate && <meta property="og:article:published_time" content={publishDate} />}
      {lastModified && <meta property="og:article:modified_time" content={lastModified} />}
      
      {/* Performance hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://images.unsplash.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      
      {/* Geo Tags */}
      <meta name="geo.region" content={`US-${location.state}`} />
      <meta name="geo.placename" content={location.city} />
      <meta name="geo.position" content="43.6468748;-116.3740863" />
      <meta name="ICBM" content="43.6468748, -116.3740863" />
      
      {/* Language */}
      <meta property="og:locale" content="en_US" />
      <meta name="language" content="English" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={`${title} | ${name}`} />
      <meta property="og:description" content={`${description} Call (208) 328-4500. Commercial low voltage serving ${location.city} and the Treasure Valley.`} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${name} - Commercial low voltage and network installation in ${location.city}, Idaho`} />
      <meta property="og:site_name" content={name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${title} | ${name}`} />
      <meta name="twitter:description" content={`${description} Commercial cabling, cameras and access control in ${location.city}, Boise and Nampa. Call (208) 328-4500.`} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${name} - Commercial low voltage and network installation in ${location.city}, Idaho`} />
    </Helmet>
  );
}